"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import toast from "react-hot-toast"
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSearch,
  FaFileExcel,
  FaTimes,
  FaShieldAlt,
  FaCamera,
  FaVideo,
  FaRedo,
} from "react-icons/fa"
import { FiFilter, FiX } from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import api from "../../libs/axios"
import { useAuth } from "../../provider/auth.provider"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { dateRangeInputBounds } from "../../utils/date-range.utils"

interface ProjectOption {
  id: number
  project_name: string
  project_short_name?: string
}

interface VisitVerificationItem {
  id: number
  inc_site_id: number
  field_work_id?: number
  visit_number: number
  visit_date?: string
  visit_status: string
  check_in_time?: string
  check_out_time?: string
  address?: string
  work_done?: string
  next_day_plan?: string
  remarks?: string
  visit_verification_status?: "pending" | "approved" | "rejected" | null
  visit_verification_remarks?: string | null
  visit_verification_reviewed_at?: string | null
  safety_verification_status?: "pending" | "approved" | "rejected" | null
  safety_verification_remarks?: string | null
  safety_verification_reviewed_at?: string | null
  created_by: number
  created_at: string
  inc_site?: {
    id: number
    site_code: string
    site_name: string
    project_id: number
    status: string
    workflow_status: string
    project?: {
      id: number
      project_name: string
      project_short_name: string
      inc_checkin_without_verification: boolean
    }
  }
  creator?: {
    id: number
    first_name: string
    last_name: string
    phone_number?: string
    email?: string
  }
  visit_verifier?: {
    id: number
    first_name: string
    last_name: string
  }
  safety_verifier?: {
    id: number
    first_name: string
    last_name: string
  }
  overall_status: "pending" | "approved" | "rejected" | "not_required"
  verification_required: boolean
  can_approve: boolean
  media: {
    total: number
    visit_verification: any | null
    photo_count: number
    video_count: number
    photos: any[]
    videos: any[]
  }
}

const statusBadge = (status?: string | null) => {
  switch (status) {
    case "approved":
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="w-3 h-3 text-green-600" /> Approved
        </span>
      )
    case "pending":
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <FaClock className="w-3 h-3 text-amber-600" /> Pending
        </span>
      )
    case "rejected":
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaTimesCircle className="w-3 h-3 text-red-600" /> Rejected
        </span>
      )
    case "not_required":
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Not Required
        </span>
      )
    default:
      return <span className="text-gray-400 text-xs">-</span>
  }
}

const visitStatusBadge = (status: string) => {
  switch (status) {
    case "completed":
      return <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700 border border-green-200">Completed</span>
    case "in_progress":
      return <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-50 text-blue-700 border border-blue-200">In Progress</span>
    case "cancelled":
      return <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-700 border border-red-200">Cancelled</span>
    default:
      return <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700 border border-gray-200">Planned</span>
  }
}

const formatDate = (v?: string | null) => {
  if (!v) return "-"
  const d = new Date(v)
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString()
}

const formatDateTime = (v?: string | null) => {
  if (!v) return "-"
  const d = new Date(v)
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
}

const SafetyVisitVerificationPage: React.FC = () => {
  const { user, hasPermission } = useAuth()
  const canReview = hasPermission("IncSite", "visit_verification_approve")
  const canExport = hasPermission("IncSite", "export_visits")

  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<VisitVerificationItem[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  // Filters
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [projectIdFilter, setProjectIdFilter] = useState("")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")

  // Projects list for filter
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)

  // Modal states
  const [mediaModalItem, setMediaModalItem] = useState<VisitVerificationItem | null>(null)
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null)
  const [approvingItem, setApprovingItem] = useState<VisitVerificationItem | null>(null)
  const [approvingSubmitting, setApprovingSubmitting] = useState(false)

  // Export
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [showExport, setShowExport] = useState(false)

  const debouncedSearchUpdate = useDebouncedCallback((val: string) => {
    setDebouncedSearch(val)
    setPage(1)
  }, 400)

  // Fetch active projects for filter dropdown
  useEffect(() => {
    let cancelled = false
    setProjectsLoading(true)
    api
      .get("/projects/list", { params: { status: "ACTIVE", assigned_only: true, limit: 100 } })
      .then((res) => {
        if (cancelled) return
        const body = res.data?.data
        const list = Array.isArray(body) ? body : body?.projects || []
        setProjects(list)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProjectsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fetchVerifications = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { page, limit }
      if (debouncedSearch) params.search = debouncedSearch
      if (statusFilter && statusFilter !== "all") params.status = statusFilter
      if (projectIdFilter) params.project_id = projectIdFilter
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate

      const res = await api.get("/inc/visit-verifications", { params })
      const data = res.data?.data || {}
      setItems(data.visits || [])
      const pg = data.pagination
      setPagination(
        pg
          ? {
              offset: (Number(pg.current_page || 1) - 1) * Number(pg.per_page || limit),
              limit: Number(pg.per_page ?? limit),
              page: Number(pg.current_page ?? 1),
              total_pages: Number(pg.total_pages ?? 1),
              total_count: Number(pg.total ?? 0),
              has_next_page: !!pg.has_next_page,
              has_prev_page: !!pg.has_prev_page,
              next_page: pg.has_next_page ? Number(pg.current_page) + 1 : null,
              prev_page: pg.has_prev_page ? Number(pg.current_page) - 1 : null,
            }
          : null,
      )
    } catch {
      toast.error("Failed to load visit verifications")
    } finally {
      setLoading(false)
    }
  }, [page, limit, debouncedSearch, statusFilter, projectIdFilter, fromDate, toDate])

  useEffect(() => {
    fetchVerifications()
  }, [fetchVerifications])

  const handleApprove = async () => {
    if (!approvingItem) return
    setApprovingSubmitting(true)
    try {
      await api.post(`/inc/visits/${approvingItem.id}/verification/review`, {
        part: "all",
        action: "approve",
        remarks: "Approved from Safety Visit Verification table",
      })
      toast.success(`Visit #${approvingItem.visit_number} verification approved successfully`)
      setApprovingItem(null)
      fetchVerifications()
    } catch {
      /* axios interceptor handles error message */
    } finally {
      setApprovingSubmitting(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, any> = { page: 1, limit: 100 }
      if (debouncedSearch) params.search = debouncedSearch
      if (statusFilter && statusFilter !== "all") params.status = statusFilter
      if (projectIdFilter) params.project_id = projectIdFilter
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate

      let allRows: VisitVerificationItem[] = []
      let p = 1
      let totalPages = 1
      do {
        const res = await api.get("/inc/visit-verifications", { params: { ...params, page: p } })
        const data = res.data?.data || {}
        const rows = data.visits || []
        allRows = allRows.concat(rows)
        totalPages = data.pagination?.total_pages || 1
        p++
      } while (p <= totalPages && p <= 10)

      const mapped = allRows.map((v) => ({
        "Site Code": v.inc_site?.site_code || "",
        "Site Name": v.inc_site?.site_name || "",
        Project: v.inc_site?.project?.project_name || "",
        "Visit Number": v.visit_number,
        "Visit Date": formatDate(v.visit_date),
        "Visit Status": v.visit_status,
        "Field Engineer": v.creator ? `${v.creator.first_name} ${v.creator.last_name}` : "",
        "Overall Verification Status": v.overall_status,
        "Visit Verification Status": v.visit_verification_status || "-",
        "Visit Verification Reviewed By": v.visit_verifier ? `${v.visit_verifier.first_name} ${v.visit_verifier.last_name}` : "",
        "Visit Verification Reviewed At": formatDateTime(v.visit_verification_reviewed_at),
        "Safety Verification Status": v.safety_verification_status || "-",
        "Safety Verification Reviewed By": v.safety_verifier ? `${v.safety_verifier.first_name} ${v.safety_verifier.last_name}` : "",
        "Safety Verification Reviewed At": formatDateTime(v.safety_verification_reviewed_at),
        "Total Media Files": v.media?.total || 0,
        "Safety Photos": v.media?.photo_count || 0,
        "Safety Videos": v.media?.video_count || 0,
        "Created At": formatDateTime(v.created_at),
      }))

      setExportData(mapped)
      setShowExport(true)
    } catch {
      toast.error("Failed to prepare export")
    } finally {
      setExporting(false)
    }
  }

  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (statusFilter && statusFilter !== "all") count++
    if (projectIdFilter) count++
    if (fromDate) count++
    if (toDate) count++
    return count
  }, [statusFilter, projectIdFilter, fromDate, toDate])

  const clearFilters = () => {
    setStatusFilter("all")
    setProjectIdFilter("")
    setFromDate("")
    setToDate("")
    setSearchTerm("")
    setDebouncedSearch("")
    setPage(1)
  }

  const bounds = dateRangeInputBounds(fromDate, toDate)

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 text-blue-800 rounded-lg">
                <FaShieldAlt className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Safety Visit Verification</h2>
                <p className="text-xs text-gray-600 mt-0.5">
                  Review and approve field check-in proof and safety verification media across INC sites
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {canExport && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-3.5 py-2 rounded flex items-center justify-center gap-2 transition-colors text-xs font-medium shadow-sm"
                >
                  <FaFileExcel className="w-3.5 h-3.5" />
                  {exporting ? "Preparing..." : "Export Excel"}
                </button>
              )}
              <button
                onClick={fetchVerifications}
                disabled={loading}
                className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-3 py-2 rounded flex items-center justify-center gap-1.5 transition-colors text-xs font-medium shadow-sm"
                title="Refresh table"
              >
                <FaRedo className={`w-3 h-3 ${loading ? "animate-spin text-blue-600" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/40">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder="Search site code, site name, engineer, visit #..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    debouncedSearchUpdate(e.target.value)
                  }}
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Verification Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Project Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project</label>
              <select
                value={projectIdFilter}
                onChange={(e) => {
                  setProjectIdFilter(e.target.value)
                  setPage(1)
                }}
                disabled={projectsLoading}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_name} {p.project_short_name ? `(${p.project_short_name})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value)
                    setPage(1)
                  }}
                  {...bounds.from}
                  className="w-1/2 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 bg-white"
                  title="From Date"
                />
                <span className="text-gray-400 text-xs">-</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value)
                    setPage(1)
                  }}
                  {...bounds.to}
                  className="w-1/2 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 bg-white"
                  title="To Date"
                />
              </div>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFiltersCount > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap text-xs">
              <span className="text-gray-600 font-medium">Active filters:</span>
              {statusFilter !== "all" && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter("all")} className="text-blue-600 hover:text-blue-800">
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
              {projectIdFilter && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  Project: {projects.find((p) => String(p.id) === projectIdFilter)?.project_name || projectIdFilter}
                  <button onClick={() => setProjectIdFilter("")} className="text-blue-600 hover:text-blue-800">
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(fromDate || toDate) && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  Date: {fromDate || "Start"} to {toDate || "End"}
                  <button
                    onClick={() => {
                      setFromDate("")
                      setToDate("")
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button onClick={clearFilters} className="text-blue-600 hover:text-blue-800 underline ml-2">
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[170px]">Actions</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[130px]">Site Code</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">Site Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Project</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[80px]">Visit #</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[100px]">Visit Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">Field Engineer</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">Overall Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Visit Verification</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">Safety Verification</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">Media</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[100px]">Visit State</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-sm text-gray-500">Loading visit verifications...</span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-gray-400">
                        <FaShieldAlt className="w-6 h-6" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">No visit verifications found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or search keywords</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/80 transition-colors">
                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* View Site button: navigates to /inc/sites/:id?tab=visits&visit_id=:visitId */}
                        <Link
                          to={`/inc/sites/${row.inc_site_id}?tab=visits&visit_id=${row.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:text-blue-800 transition-colors shadow-xs"
                          title="Open INC Site and expand this visit"
                        >
                          <LuEye className="w-3.5 h-3.5" />
                          <span>View Site</span>
                        </Link>

                        {/* Approve button */}
                        {row.can_approve ? (
                          <button
                            onClick={() => setApprovingItem(row)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors shadow-xs"
                            title="Approve visit and safety verification"
                          >
                            <FaCheckCircle className="w-3 h-3" />
                            <span>Approve</span>
                          </button>
                        ) : row.overall_status === "approved" ? (
                          <span className="inline-flex items-center gap-0.5 text-[11px] text-green-700 font-medium">
                            <FaCheckCircle className="w-3 h-3 text-green-600" /> Done
                          </span>
                        ) : Number(row.created_by) === Number(user?.id) ? (
                          <span className="text-[11px] text-gray-400 italic" title="You cannot approve your own visit">
                            Own visit
                          </span>
                        ) : row.visit_status === "completed" || row.visit_status === "cancelled" ? (
                          <span className="text-[11px] text-gray-400 italic">Closed</span>
                        ) : null}
                      </div>
                    </td>

                    {/* Site Code */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/inc/sites/${row.inc_site_id}`}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                      >
                        {row.inc_site?.site_code || "-"}
                      </Link>
                    </td>

                    {/* Site Name */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-800 truncate max-w-[160px] block" title={row.inc_site?.site_name}>
                        {row.inc_site?.site_name || "-"}
                      </span>
                    </td>

                    {/* Project */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-700 truncate max-w-[140px] block" title={row.inc_site?.project?.project_name}>
                        {row.inc_site?.project?.project_name || "-"}
                      </span>
                    </td>

                    {/* Visit # */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-800">
                        #{row.visit_number}
                      </span>
                    </td>

                    {/* Visit Date */}
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">
                      {formatDate(row.visit_date || row.created_at)}
                    </td>

                    {/* Field Engineer */}
                    <td className="px-4 py-3">
                      <div className="text-xs font-medium text-gray-900 truncate max-w-[150px]">
                        {row.creator ? `${row.creator.first_name} ${row.creator.last_name}` : "-"}
                      </div>
                      {row.creator?.phone_number && (
                        <div className="text-[11px] text-gray-500">{row.creator.phone_number}</div>
                      )}
                    </td>

                    {/* Overall Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {statusBadge(row.overall_status)}
                    </td>

                    {/* Visit Verification */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>{statusBadge(row.visit_verification_status)}</div>
                      {row.visit_verifier && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          by {row.visit_verifier.first_name}
                        </div>
                      )}
                    </td>

                    {/* Safety Verification */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>{statusBadge(row.safety_verification_status)}</div>
                      {row.safety_verifier && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          by {row.safety_verifier.first_name}
                        </div>
                      )}
                    </td>

                    {/* Media summary + view */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.media?.total > 0 ? (
                        <button
                          onClick={() => setMediaModalItem(row)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded transition-colors"
                          title="Click to view submitted photos/videos"
                        >
                          <FaCamera className="w-3 h-3 text-indigo-600" />
                          <span>{row.media.total} files</span>
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No files</span>
                      )}
                    </td>

                    {/* Visit State */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {visitStatusBadge(row.visit_status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.total_count > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white">
            <Pagination
              pagination={pagination}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
              limitOptions={[10, 25, 50, 100]}
            />
          </div>
        )}
      </div>

      {/* Confirmation Modal for Approve */}
      {approvingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="text-base font-semibold text-gray-900">Approve Verification</h3>
              </div>
              <button
                onClick={() => setApprovingItem(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>
                Are you sure you want to approve both <strong>Visit Verification</strong> and <strong>Safety Verification</strong> for:
              </p>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-1 text-xs">
                <div><span className="text-gray-500">Site Code:</span> <span className="font-semibold">{approvingItem.inc_site?.site_code}</span></div>
                <div><span className="text-gray-500">Visit Number:</span> <span className="font-semibold">#{approvingItem.visit_number}</span></div>
                <div><span className="text-gray-500">Field Engineer:</span> <span className="font-semibold">{approvingItem.creator ? `${approvingItem.creator.first_name} ${approvingItem.creator.last_name}` : "-"}</span></div>
                <div><span className="text-gray-500">Files Submitted:</span> <span className="font-semibold">{approvingItem.media?.total || 0} file(s)</span></div>
              </div>
              <p className="text-xs text-gray-500">
                Approving this verification will unlock check-in for the field engineer if check-in was pending verification approval.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApprovingItem(null)}
                disabled={approvingSubmitting}
                className="px-3.5 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={approvingSubmitting}
                className="px-4 py-1.5 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {approvingSubmitting ? "Approving..." : "Confirm Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Gallery Preview Modal */}
      {mediaModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Verification Media — Site {mediaModalItem.inc_site?.site_code} (Visit #{mediaModalItem.visit_number})
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Engineer: {mediaModalItem.creator ? `${mediaModalItem.creator.first_name} ${mediaModalItem.creator.last_name}` : "-"} · {mediaModalItem.media.total} media item(s)
                </p>
              </div>
              <button
                onClick={() => {
                  setMediaModalItem(null)
                  setSelectedMediaUrl(null)
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto space-y-6 flex-1">
              {/* Visit Verification Image */}
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-600 tracking-wider mb-2 flex items-center gap-1.5">
                  <FaCamera className="w-3.5 h-3.5 text-blue-600" />
                  Visit Verification Photo
                </h4>
                {mediaModalItem.media.visit_verification ? (
                  <div className="max-w-xs border rounded overflow-hidden bg-gray-50 hover:shadow-md transition-shadow">
                    <img
                      src={mediaModalItem.media.visit_verification.file_url}
                      alt="Visit verification"
                      className="w-full h-48 object-cover cursor-pointer hover:opacity-95"
                      onClick={() => setSelectedMediaUrl(mediaModalItem.media.visit_verification.file_url)}
                    />
                    <div className="p-2 text-xs text-gray-600 flex justify-between items-center bg-white border-t">
                      <span className="truncate max-w-[200px]" title={mediaModalItem.media.visit_verification.file_name}>
                        {mediaModalItem.media.visit_verification.file_name || "Verification Photo"}
                      </span>
                      <a
                        href={mediaModalItem.media.visit_verification.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-[11px]"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No visit verification photo uploaded</p>
                )}
              </div>

              {/* Safety Photos */}
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-600 tracking-wider mb-2 flex items-center gap-1.5">
                  <FaCamera className="w-3.5 h-3.5 text-indigo-600" />
                  Safety Verification Photos ({mediaModalItem.media.photo_count})
                </h4>
                {mediaModalItem.media.photos.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {mediaModalItem.media.photos.map((p, idx) => (
                      <div key={p.id || idx} className="border rounded overflow-hidden bg-gray-50 hover:shadow-md transition-shadow">
                        <img
                          src={p.file_url}
                          alt={p.file_name || `Safety photo ${idx + 1}`}
                          className="w-full h-32 object-cover cursor-pointer hover:opacity-95"
                          onClick={() => setSelectedMediaUrl(p.file_url)}
                        />
                        <div className="p-1.5 text-[11px] text-gray-600 flex justify-between items-center bg-white border-t">
                          <span className="truncate max-w-[120px]" title={p.file_name}>
                            {p.file_name || `Photo ${idx + 1}`}
                          </span>
                          <a href={p.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                            Open
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No safety photos uploaded</p>
                )}
              </div>

              {/* Safety Videos */}
              {mediaModalItem.media.video_count > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase text-gray-600 tracking-wider mb-2 flex items-center gap-1.5">
                    <FaVideo className="w-3.5 h-3.5 text-rose-600" />
                    Safety Verification Videos ({mediaModalItem.media.video_count})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mediaModalItem.media.videos.map((v, idx) => (
                      <div key={v.id || idx} className="border rounded overflow-hidden bg-black p-1">
                        <video src={v.file_url} controls className="w-full max-h-56" />
                        <div className="p-1.5 text-[11px] text-gray-300 flex justify-between items-center bg-gray-900">
                          <span className="truncate max-w-[180px]" title={v.file_name}>{v.file_name || `Video ${idx + 1}`}</span>
                          <a href={v.file_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <Link
                to={`/inc/sites/${mediaModalItem.inc_site_id}?tab=visits&visit_id=${mediaModalItem.id}`}
                className="text-xs text-blue-700 hover:underline flex items-center gap-1"
              >
                <LuEye className="w-3.5 h-3.5" /> Open this visit in Site View
              </Link>
              <button
                onClick={() => {
                  setMediaModalItem(null)
                  setSelectedMediaUrl(null)
                }}
                className="px-4 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-size Image Zoom Modal */}
      {selectedMediaUrl && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedMediaUrl(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img src={selectedMediaUrl} alt="Preview" className="max-w-full max-h-[85vh] rounded object-contain shadow-2xl" />
            <button
              onClick={() => setSelectedMediaUrl(null)}
              className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black text-white rounded-full"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ExcelDownload component for exporting */}
      {showExport && (
        <ExcelDownload
          isOpen={showExport}
          data={exportData}
          filename="safety_visit_verifications"
          title="Export Safety Visit Verifications"
          description="Download safety and visit verification details"
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  )
}

export default SafetyVisitVerificationPage
